import { useState, useEffect, useRef } from 'react';
import type { TaAgentVariant } from '@/contexts/PageContext';
import { Button } from '@/components/ui/button';
import supportIALogo from '@/assets/supportIA.png';
// Solo el Agente TA Metrics tiene avatar propio (Sol 18/09); el resto sigue con supportIA.
import taAgentAvatar from '@/assets/ta-agent-avatar.png';
import { useTranslation } from 'react-i18next';
import { useTaSuggestedQuestions } from '@/components/talent-acquisition/ta-assistant-questions';
import { useOmlSuggestedQuestions } from '@/components/owned-media/oml-assistant-questions';

const PARTNERSHIPS_QUESTIONS = [
  '¿Qué paquetes tienen menor inversión mínima?',
  '¿Dónde están los one-pagers?',
  '¿Cuándo usar Branded Buzz vs Prime Time?',
];

interface PartnerCtx {
  partnerId: string;
  partnerName: string;
  partnerSlug: string;
}

interface AssistantFABProps {
  onClick: () => void;
  unreadCount?: number;
  partnerCtx?: PartnerCtx | null;
  onPartnershipQuestion?: (q: string) => void;
  /** People → Talent Acquisition: supportIA pasa a modo Agente TA Metrics (mismo mecanismo que Partnerships). */
  taActive?: boolean;
  /** Qué universo atiende el bot de TA: la base entera o solo las búsquedas de quien pregunta. */
  taScope?: TaAgentVariant;
  onTaQuestion?: (q: string) => void;
  /** Owned Media: supportIA responde sobre el apartado. */
  omlActive?: boolean;
  onOmlQuestion?: (q: string) => void;
}

export function AssistantFAB({ onClick, unreadCount, partnerCtx, onPartnershipQuestion, taActive, taScope = 'metrics', onTaQuestion, omlActive, onOmlQuestion }: AssistantFABProps) {
  const { t } = useTranslation();
  const preguntas = useTaSuggestedQuestions(taScope);
  const [showBubble, setShowBubble] = useState(false);
  const prevSectionRef = useRef<string | null>(null);
  // Qué sección "rica" está activa: la burbuja con preguntas se muestra una vez por sección
  const sectionKey = partnerCtx ? `partner:${partnerCtx.partnerId}` : taActive ? `ta:${taScope}` : omlActive ? 'oml' : null;
  const omlQuestions = useOmlSuggestedQuestions();

  useEffect(() => {
    if (sectionKey) {
      // New partner page / TA section — show the rich bubble
      if (prevSectionRef.current !== sectionKey) {
        prevSectionRef.current = sectionKey;
        setShowBubble(false);
        const show = setTimeout(() => setShowBubble(true), 1500);
        const hide = setTimeout(() => setShowBubble(false), 12000);
        return () => { clearTimeout(show); clearTimeout(hide); };
      }
    } else {
      prevSectionRef.current = null;
      // Default bubble for the rest of the pages
      setShowBubble(false);
      const show = setTimeout(() => setShowBubble(true), 3000);
      const hide = setTimeout(() => setShowBubble(false), 10000);
      return () => { clearTimeout(show); clearTimeout(hide); };
    }
  }, [sectionKey]);

  const isPartnerships = !!partnerCtx;
  const isTa = !isPartnerships && !!taActive;
  const isOml = !isPartnerships && !isTa && !!omlActive;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
      {showBubble && (
        <div className="relative">
          {isPartnerships ? (
            <RichBubble
              title={t('assistant.assistantfab.hub_de', { name: partnerCtx.partnerName })}
              prompt={t('assistant.assistantfab.en_que_te_ayudo')}
              questions={PARTNERSHIPS_QUESTIONS}
              onOpen={onClick}
              onPick={(q) => { setShowBubble(false); onPartnershipQuestion?.(q); }}
            />
          ) : isTa ? (
            // Talent Acquisition: preguntas del Agente TA Metrics, en el idioma de la pantalla
            <RichBubble
              wide
              title={t(taScope === 'intake' ? 'talent_acquisition.assistant_tab.mine_title' : 'assistant.assistantfab.ta_title')}
              prompt={t(taScope === 'intake' ? 'talent_acquisition.assistant_tab.mine_prompt' : 'assistant.assistantfab.ta_prompt')}
              questions={preguntas.slice(0, 3)}
              onOpen={onClick}
              onPick={(q) => { setShowBubble(false); onTaQuestion?.(q); }}
            />
          ) : isOml ? (
            // Owned Media (08/10/2026): supportIA responde sobre las cuentas y el Inicio del apartado
            <RichBubble
              wide
              title={t('oml.assistant.title', 'Owned Media')}
              prompt={t('oml.assistant.prompt', '¿Qué querés saber del apartado?')}
              questions={omlQuestions}
              onOpen={onClick}
              onPick={(q) => { setShowBubble(false); onOmlQuestion?.(q); }}
            />
          ) : (
            // Default bubble — texto simple
            <div
              className="bg-white dark:bg-zinc-800 text-foreground text-sm px-4 py-2 rounded-2xl rounded-br-sm shadow-lg border border-border animate-in fade-in slide-in-from-bottom-2 duration-300 max-w-[200px] cursor-pointer"
              onClick={onClick}
            >
              {t('assistant.bubble_prompt')}
              <div className="absolute -bottom-1.5 right-4 w-3 h-3 bg-white dark:bg-zinc-800 border-r border-b border-border rotate-45" />
            </div>
          )}
        </div>
      )}

      <div className="relative">
        <Button
          onClick={onClick}
          size="lg"
          className="h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all p-0 overflow-hidden"
          title={t('assistant.open_title')}
          // El nombre accesible salía del alt de la imagen ("supportIA" / el del agente):
          // con aria-label el botón se anuncia por lo que HACE, y el bot de staging lo encuentra.
          aria-label={t('assistant.open_title')}
        >
          <img
            src={isTa ? taAgentAvatar : supportIALogo}
            alt={isTa ? t('assistant.assistantfab.ta_title') : 'supportIA'}
            className="h-full w-full object-cover"
          />
        </Button>
        {unreadCount && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </div>
    </div>
  );
}

/** Burbuja con encabezado que abre el chat y hasta 3 preguntas clickeables (Partnerships, TA, OML). */
function RichBubble({ title, prompt, questions, onOpen, onPick, wide }: { title: string; prompt: string; questions: string[]; onOpen: () => void; onPick: (q: string) => void; wide?: boolean }) {
  return (
    <div className={`bg-white dark:bg-zinc-800 text-foreground rounded-2xl rounded-br-sm shadow-lg border border-border animate-in fade-in slide-in-from-bottom-2 duration-300 ${wide ? 'w-72 max-w-[calc(100vw-3rem)]' : 'w-64'} overflow-hidden`}>
      {/* El encabezado abre el chat: "¿En qué te ayudo?" invita a clickear */}
      <button type="button" onClick={onOpen} className="w-full text-left px-4 pt-3 pb-2 hover:bg-muted/60 transition-colors">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</p>
        <p className="text-sm font-medium mt-0.5">{prompt}</p>
      </button>
      <div className="border-t">
        {questions.map((q) => (
          <button key={q} onClick={() => onPick(q)} className="w-full text-left px-4 py-2.5 text-xs hover:bg-muted transition-colors border-b last:border-b-0">
            {q}
          </button>
        ))}
      </div>
      <div className="absolute -bottom-1.5 right-4 w-3 h-3 bg-white dark:bg-zinc-800 border-r border-b border-border rotate-45" />
    </div>
  );
}
